import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { filterByPermission } from "@/lib/permissions";
import { LayoutDashboard, Inbox, Briefcase, Clock, Bell, User, Settings, LogOut, Receipt, Calendar as CalIcon } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_apex-merchants/artifacts/6e4jsq53_New%20FieldProIQ%20logo.PNG";

const navItems = [
  { to: "/vendor",          icon: LayoutDashboard, label: "Home",     end: true, perm: "tech.home" },
  { to: "/vendor/active",   icon: Briefcase,       label: "Active",              perm: "tech.active" },
  { to: "/vendor/schedule", icon: CalIcon,         label: "Schedule",            perm: "tech.schedule" },
  { to: "/vendor/expenses", icon: Receipt,         label: "Expenses",            perm: "tech.expenses" },
  { to: "/vendor/history",  icon: Clock,           label: "History",             perm: "tech.history" },
];

export default function VendorLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Top Header */}
      <header className="glass-header sticky top-0 z-40 px-4 py-3">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src={LOGO_URL} alt="Apex" className="h-14 w-auto object-contain" />
          </div>
          <div className="flex items-center gap-2">
            <NavLink
              to="/vendor/profile"
              className="w-8 h-8 rounded-full bg-slate-900/5 flex items-center justify-center hover:bg-slate-900/10 transition-colors"
              data-testid="vendor-profile-link"
            >
              <User className="w-4 h-4 text-slate-700" />
            </NavLink>
            <NavLink
              to="/vendor/settings"
              className="w-8 h-8 rounded-full bg-slate-900/5 flex items-center justify-center hover:bg-slate-900/10 transition-colors"
              data-testid="vendor-settings-link"
            >
              <Settings className="w-4 h-4 text-slate-700" />
            </NavLink>
            <button
              onClick={handleLogout}
              className="w-8 h-8 rounded-full bg-slate-900/5 flex items-center justify-center hover:bg-red-500/15 hover:text-red-600 transition-colors text-slate-700"
              data-testid="vendor-logout-button"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-lg mx-auto px-4 pt-4 pb-24">
        <Outlet />
      </main>

      {/* Bottom Navigation */}
      <nav className="bottom-nav" data-testid="vendor-bottom-nav">
        <div className="max-w-lg mx-auto flex items-center justify-around px-2 py-2">
          {filterByPermission(navItems, user).map(({ to, icon: Icon, label, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 px-3 py-1.5 rounded-xl transition-all min-w-[52px] ${
                  isActive
                    ? "text-red-600 bg-red-500/15"
                    : "text-slate-600 hover:text-slate-900"
                }`
              }
              data-testid={`nav-${label.toLowerCase()}`}
            >
              <Icon className="w-5 h-5" />
              <span className="text-[10px] font-medium">{label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
}
